import { useAuth } from "@/auth/AuthContext";
import { hasRole } from "@/lib/hasRole";
import { useCallback, useState } from "react";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router";
import { Button } from "@/components/Button";
import { TabNav } from "@/components/TabNav";
import { plural } from "@/lib/plural";

const tabs = [
  {
    label: "Virtual machines",
    to: "virtual-machines",
    typeLabelSentence: "virtual machine",
  },
  { label: "Databases", to: "databases", typeLabelSentence: "database" },
  {
    label: "Kubernetes clusters",
    to: "kubernetes-clusters",
    typeLabelSentence: "Kubernetes cluster",
  },
];

export interface ResourcesSummary {
  total: number;
  needsAttention: number;
  typeLabelPlural: string;
}

export interface ResourcesLayoutContext {
  setSummary: (summary: ResourcesSummary | null) => void;
  openCreate: () => void;
}

export function ResourcesLayout() {
  const { user } = useAuth();
  const isAdmin = hasRole(user?.roles, "Admin");
  const { orgId } = useParams();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<ResourcesSummary | null>(null);

  const activeTab =
    tabs.find((tab) => location.pathname.endsWith(`/${tab.to}`)) ?? tabs[0]!;

  const openCreate = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("create", "1");

        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return (
    <div className="text-foreground">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Resources</h1>
          {summary && (
            <p className="mt-0.5 text-sm text-foreground/70">
              {plural(summary.total, summary.typeLabelPlural.replace(/s$/, ""))}
              {isAdmin &&
                summary.needsAttention > 0 &&
                ` · ${summary.needsAttention} need${summary.needsAttention === 1 ? "s" : ""} attention`}
            </p>
          )}
        </div>
        {isAdmin && (
          <Button variant="primary" onClick={openCreate}>
            Add {activeTab.typeLabelSentence}
          </Button>
        )}
      </div>

      <TabNav tabs={tabs} basePath={`/${orgId}/resources`} />

      <Outlet
        context={{ setSummary, openCreate } satisfies ResourcesLayoutContext}
      />
    </div>
  );
}
